/**
 * Generic resource-listing screen for the Manpower P&L module.
 *
 * Extracted from the (previously duplicated) Vendor & Worker listings: same page shell,
 * filter row, 4-state table (loading / error / empty / rows), row actions, and toast —
 * the only differences between screens are columns, filters, actions, and the data hook,
 * so those are passed in as config. New CRUD-shaped screens (vehicles…) are now thin config.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../../pages/email-workflows/WorkflowTable';

export interface Column<T> {
	/** Header label. */
	header: string;
	/** Cell renderer for a row. */
	render: (row: T) => React.ReactNode;
	/** Right-align numeric columns. */
	align?: 'left' | 'right';
	/** Phone table role (owner 2026-07-06: # · name · the numbers that matter —
	 *  status/city/id live in filters, never in rows). Defaults: 'title' for the
	 *  first /name/i column, 'value' for right-aligned columns, 'off' otherwise.
	 *  'subtitle' = a small grey line under the title (e.g. a worker's vendor). */
	mobile?: 'title' | 'subtitle' | 'value' | 'off';
}

export interface RowAction<T> {
	label: string;
	onClick: (row: T) => void;
	/** Tailwind text color class, e.g. 'text-blue-600'. */
	colorClass?: string;
	/** Icon for the phone table (owner: icons, not words, in rows). */
	icon?: React.ReactNode;
}

export interface FilterConfig {
	label: string;
	value: number | string | '';
	onChange: (value: number | string | '') => void;
	options: { id: number | string; label: string }[];
	/** Text for the "all" option (e.g. "All cities"). */
	allLabel: string;
}

interface ResourceListingProps<T> {
	title: string;
	/** Subtitle without the count; count is appended automatically when loaded. */
	subtitle: string;
	addLabel?: string;
	onAdd?: () => void;
	/** One or more dropdown filters (status/city/etc live HERE, never in rows). */
	filter?: FilterConfig | FilterConfig[];
	columns: Column<T>[];
	rows: T[];
	rowKey: (row: T, index: number) => React.Key;
	actions?: RowAction<T>[];
	isLoading: boolean;
	error?: Error | null;
	/** Toast message (rendered bottom-right when set). */
	toast?: string | null;
	/** Modals etc. rendered as overlays. */
	children?: React.ReactNode;
	/** Opt-in phone table (# · name · numbers). Owner 2026-07-06: approve it on
	 *  Vendors FIRST, then flip it on per screen — one line each. Off = the
	 *  original desktop table everywhere (pre-redesign behavior). */
	phoneTable?: boolean;
	/** PHONE-ONLY: tapping a row does this (e.g. drill into a vendor's workers).
	 *  Desktop is untouched (byte-identical). Action buttons stop propagation. */
	onRowClick?: (row: T) => void;
	/** Back link rendered above the title (e.g. { label: 'Vendors', href: '/manpower/vendors' }). */
	backTo?: { label: string; href: string };
}

export function ResourceListing<T>({
	title,
	subtitle,
	addLabel,
	onAdd,
	filter,
	columns,
	rows,
	rowKey,
	actions,
	isLoading,
	error,
	toast,
	children,
	phoneTable = false,
	onRowClick,
	backTo,
}: ResourceListingProps<T>) {
	const colCount = columns.length + (actions && actions.length ? 1 : 0);
	const ready = !isLoading && !error;
	// phone (<sm): card list, v2 look (owner 2026-07-06) — JS switch, same rule as
	// WorkflowTable: never CSS-hide two trees. Cards derive from the column config:
	// first column = title, first right-aligned column = the key number, rest = fields.
	const isMobile = useIsMobile();
	const titleCol =
		columns.find(c => c.mobile === 'title') ?? columns.find(c => /name/i.test(c.header)) ?? columns[0];
	const subtitleCol = columns.find(c => c.mobile === 'subtitle');
	const valueCols = columns.filter(
		c => c !== titleCol && (c.mobile === 'value' || (c.mobile == null && c.align === 'right'))
	);

	return (
		// phone table mode: the screen is narrow — spend it on data, not padding
		<div className={`min-h-screen bg-white ${isMobile && phoneTable ? 'p-3' : 'p-6'}`}>
			<div className='max-w-7xl mx-auto'>
				<div className='mb-6 flex items-start justify-between'>
					<div>
						{backTo && (
							<Link to={backTo.href} className='mb-1 inline-block text-[13px] font-medium text-gray-500 hover:text-gray-800'>
								‹ {backTo.label}
							</Link>
						)}
						<h1 className='text-xl font-semibold text-gray-900'>{title}</h1>
						<p className='text-sm text-gray-500'>
							{subtitle} {ready ? `(${rows.length})` : ''}
						</p>
					</div>
					{onAdd && addLabel && (
						<button
							type='button'
							onClick={onAdd}
							className='rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700'
						>
							{addLabel}
						</button>
					)}
				</div>

				{filter && (isMobile && phoneTable ? (
					// phone: options you can SEE and tap (owner ref: iOS menu pattern).
					// ≤3 options → pills, one tap, no dropdown. Long lists → a chip select.
					<div className='mb-3 flex flex-wrap items-center gap-2'>
						{(Array.isArray(filter) ? filter : [filter]).map((f, fi) =>
							f.options.length <= 3 ? (
								<div key={fi} role='group' aria-label={f.label} className='inline-flex rounded-full border border-gray-200 p-0.5'>
									{[{ id: '' as const, label: 'All' }, ...f.options].map(o => (
										<button
											key={String(o.id)}
											type='button'
											onClick={() => f.onChange(o.id)}
											aria-pressed={f.value === o.id}
											className={`rounded-full px-3 py-1 text-xs font-medium ${
												f.value === o.id ? 'bg-gray-900 text-white' : 'text-gray-600'
											}`}
										>
											{o.label}
										</button>
									))}
								</div>
							) : (
								<select
									key={fi}
									aria-label={f.label}
									value={f.value}
									onChange={e => {
										const match = f.options.find(o => String(o.id) === e.target.value);
										f.onChange(e.target.value === '' ? '' : (match?.id ?? e.target.value));
									}}
									className='rounded-full border border-gray-300 bg-white py-1.5 pl-3 pr-7 text-[13px] font-medium text-gray-800 focus:outline-none'
								>
									<option value=''>{f.allLabel}</option>
									{f.options.map(o => (
										<option key={o.id} value={o.id}>
											{o.label}
										</option>
									))}
								</select>
							)
						)}
					</div>
				) : (
					<div className='mb-4 flex flex-wrap items-center gap-x-4 gap-y-2'>
						{(Array.isArray(filter) ? filter : [filter]).map((f, fi) => (
							<div key={fi} className='flex items-center gap-2'>
								<label className='text-xs font-medium text-gray-700'>{f.label}</label>
								<select
									value={f.value}
									onChange={e => {
										// option ids may be numbers (city) or strings (status) — hand back the original
										const match = f.options.find(o => String(o.id) === e.target.value);
										f.onChange(e.target.value === '' ? '' : (match?.id ?? e.target.value));
									}}
									className='w-full sm:w-auto rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none'
								>
									<option value=''>{f.allLabel}</option>
									{f.options.map(o => (
										<option key={o.id} value={o.id}>
											{o.label}
										</option>
									))}
								</select>
							</div>
						))}
					</div>
				))}

				{isMobile && phoneTable ? (
					// phone = a CLEAN table (owner refs 2026-07-06: standings/medal-count
					// pattern): # · name · numbers · icon actions. No border, full-bleed
					// width (2px from the screen edge: page p-3 minus -mx-2.5).
					<div className='-mx-2.5 bg-white'>
						{isLoading ? (
							<div className='p-6 text-center text-sm text-gray-500'>Loading…</div>
						) : error ? (
							<div className='p-6 text-center text-sm text-red-600'>Failed to load: {error.message}</div>
						) : rows.length === 0 ? (
							<div className='p-6 text-center text-sm text-gray-500'>No records found.</div>
						) : (
							<table className='w-full'>
								<thead>
									<tr className='text-[10px] uppercase tracking-wide text-gray-400'>
										<th className='w-6 py-2 pl-2 pr-1 text-left font-medium'>#</th>
										<th className='py-2 pr-1 text-left font-medium'>{titleCol.header}</th>
										{valueCols.map((c, ci) => (
											<th key={ci} className='w-px whitespace-nowrap py-2 pl-1.5 pr-1.5 text-right font-medium'>
												{c.header}
											</th>
										))}
										{actions && actions.length > 0 && <th className='w-px py-2 pr-2' aria-label='Actions' />}
									</tr>
								</thead>
								<tbody className='divide-y divide-gray-100'>
									{rows.map((row, i) => (
										<tr
											key={rowKey(row, i)}
											data-testid='resource-card'
											onClick={onRowClick ? () => onRowClick(row) : undefined}
											className={onRowClick ? 'cursor-pointer active:bg-gray-50' : undefined}
										>
											<td className='py-2.5 pl-2 pr-1 text-[11px] tabular-nums text-gray-400'>{i + 1}</td>
											<td className='max-w-0 py-2.5 pr-1'>
												<div className='truncate text-[13px] font-semibold text-gray-900'>{titleCol.render(row)}</div>
												{subtitleCol && (
													<div className='truncate text-[11px] text-gray-500'>{subtitleCol.render(row)}</div>
												)}
											</td>
											{valueCols.map((c, ci) => (
												<td key={ci} className='w-px whitespace-nowrap py-2.5 pl-1.5 pr-1.5 text-right text-[13px] tabular-nums text-gray-700'>
													{c.render(row)}
												</td>
											))}
											{actions && actions.length > 0 && (
												<td className='w-px whitespace-nowrap py-1 pr-1 text-right'>
													{actions.map((a, ai) => (
														<button
															key={ai}
															type='button'
															onClick={e => { e.stopPropagation(); a.onClick(row); }}
															aria-label={a.label}
															title={a.label}
															className={`rounded-md p-2 active:bg-gray-100 ${a.colorClass ?? 'text-blue-600'}`}
														>
															{a.icon ?? <span className='text-xs font-medium'>{a.label}</span>}
														</button>
													))}
												</td>
											)}
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				) : (
				<div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
					<div className='overflow-x-auto'>
						<table className='w-full border-collapse'>
							<thead>
								<tr className='bg-gray-50 border-b border-gray-200'>
									{columns.map((c, i) => (
										<th
											key={i}
											className={`px-4 py-3 text-xs font-bold text-gray-900 ${
												c.align === 'right' ? 'text-right' : 'text-left'
											}`}
										>
											{c.header}
										</th>
									))}
									{actions && actions.length > 0 && (
										<th className='px-4 py-3 text-right text-xs font-bold text-gray-900'>Actions</th>
									)}
								</tr>
							</thead>
							<tbody>
								{isLoading ? (
									<tr>
										<td colSpan={colCount} className='px-4 py-8 text-center text-gray-500 text-sm'>
											Loading…
										</td>
									</tr>
								) : error ? (
									<tr>
										<td colSpan={colCount} className='px-4 py-8 text-center text-red-600 text-sm'>
											Failed to load: {error.message}
										</td>
									</tr>
								) : rows.length === 0 ? (
									<tr>
										<td colSpan={colCount} className='px-4 py-8 text-center text-gray-500 text-sm'>
											No records found.
										</td>
									</tr>
								) : (
									rows.map((row, i) => (
										<tr key={rowKey(row, i)} className='border-b border-gray-200 hover:bg-gray-50'>
											{columns.map((c, ci) => (
												<td
													key={ci}
													className={`px-4 py-3 text-xs text-gray-900 ${
														c.align === 'right' ? 'text-right' : ''
													}`}
												>
													{c.render(row)}
												</td>
											))}
											{actions && actions.length > 0 && (
												<td className='px-4 py-3 text-right'>
													{actions.map((a, ai) => (
														<button
															key={ai}
															type='button'
															onClick={() => a.onClick(row)}
															className={`${ai > 0 ? 'ml-3' : ''} text-xs font-medium hover:underline ${
																a.colorClass ?? 'text-blue-600'
															}`}
														>
															{a.label}
														</button>
													))}
												</td>
											)}
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
				)}
			</div>

			{children}

			{toast && (
				<div className='fixed bottom-6 right-6 z-50 rounded-md bg-gray-900 px-4 py-2 text-sm text-white shadow-lg'>
					{toast}
				</div>
			)}
		</div>
	);
}

/** Small status pill reused across manpower tables. */
export const StatusPill: React.FC<{ text?: string; tone?: 'green' | 'gray' | 'blue' }> = ({
	text,
	tone = 'gray',
}) => {
	const tones = {
		green: 'bg-green-50 text-green-700',
		gray: 'bg-gray-100 text-gray-600',
		blue: 'bg-blue-50 text-blue-700',
	};
	return (
		<span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}>
			{text ?? '—'}
		</span>
	);
};
